import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import React, { useMemo } from 'react'
import {
    useSuggestionCardFilterHandler,
    useTrackChangeCards,
} from '../../hooks'
import { useTrackChangesCardStyles } from '../../styles'
import { LoadingCardSkeletons } from './LoadingCardsSkeleton'
import { NoChangesMessage } from './NoChangesMessage'
import { VirtualizedSuggestionList } from './VirtualizedSuggestionList/VirtualizedSuggestionList'

/**
 * CardListComponent renders a list of track change cards based on the provided track changes data.
 * It uses memoization to optimize performance and avoid unnecessary re-renders.
 * @param {Record<string, TrackedChange>} props.trackChangesData - The track changes data to be displayed in the card list.
 * @return {JSX.Element} The rendered card list component.
 * */
export const CardList = React.memo(
    ({
        trackChangesData,
    }: {
        trackChangesData: Record<string, TrackedChange>
    }) => {
        useSuggestionCardFilterHandler()
        const trackChangeCardsResult = useTrackChangeCards() || {}
        const {
            shouldShowLoadingCards = false,
            isNotDefaultSuggestionCardFilter = false,
            suggestionsData = {},
        } = trackChangeCardsResult
        const isTreeLoading =
            useShallowSelector((s) => s?.isTreeLoading) ?? false
        const elementType =
            useElementItemShallowSelector(
                (state) => state?.elementItemValue?.elementType
            ) ?? ''
        const flattenedSuggestions = useMemo(
            () =>
                Object.values(suggestionsData).flatMap(
                    (group) => group?.suggestions || []
                ),
            [suggestionsData]
        )

        const classes = useTrackChangesCardStyles({
            isNotDefaultSuggestionCardFilter,
        })
        const isAssemblyElement = useMemo(
            () =>
                (elementType ?? '').toUpperCase() ===
                ProjectElementType.ASSEMBLY,
            [elementType]
        )
        const shouldShowEmpty = useMemo(
            () =>
                (flattenedSuggestions?.length ?? 0) === 0 &&
                !(shouldShowLoadingCards ?? false) &&
                !(isTreeLoading ?? false),
            [
                flattenedSuggestions?.length,
                shouldShowLoadingCards,
                isTreeLoading,
            ]
        )

        if (shouldShowLoadingCards) {
            return (
                <LoadingCardSkeletons data-testid="ant-skeleton" level={10} />
            )
        }

        if (isAssemblyElement || shouldShowEmpty) {
            return <NoChangesMessage />
        }

        return (
            <div className={classes.container}>
                <VirtualizedSuggestionList
                    suggestions={flattenedSuggestions}
                    trackChangesData={trackChangesData}
                    isCustomFilterActive={isNotDefaultSuggestionCardFilter}
                    containerHeight={1000}
                />
            </div>
        )
    }
)
